import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import FeaturedDocument from './FeaturedDocument';
import RowFactory from './RowFactory';
import {
  LiaArrowsAltVSolid,
  LiaLongArrowAltDownSolid,
  LiaLongArrowAltUpSolid,
} from 'react-icons/lia';
import { IoInformationCircle } from 'react-icons/io5';
import DebouncedInput from './Input';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { columnEnhancers } from './Builders';
import { defaultColumnEnhancer } from './Builders/default';
import { getFilterComponent } from './Filters';
import {
  extractModuleFromALevelRow,
  extractChapterFromALevelRow,
  extractGradeFromGcseRow,
} from './helpers';
import ReactMarkdown from 'react-markdown';

// Portal-based tooltip that stays attached to trigger during scroll
const PortalTooltip = ({ triggerRef, children, isOpen, onClose }) => {
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const tooltipRef = useRef(null);
  const rafRef = useRef(null);

  const updatePosition = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 8,
        left: rect.left + rect.width / 2,
      });
    }
  }, [triggerRef]);

  useEffect(() => {
    if (!isOpen) return;

    // Initial position
    updatePosition();

    // Smooth position updates using RAF
    const handleScrollOrResize = () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      rafRef.current = requestAnimationFrame(updatePosition);
    };

    // Listen to all scroll events (capture phase to catch all scrollable containers)
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);

    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [isOpen, updatePosition]);

  if (!isOpen) return null;

  return createPortal(
    <>
      {/* Invisible backdrop for click-outside detection */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 99999999999,
        }}
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
      />
      {/* Tooltip */}
      <div
        ref={tooltipRef}
        style={{
          position: 'fixed',
          top: position.top,
          left: position.left,
          transform: 'translateX(-50%)',
          zIndex: 99999999,
          backgroundColor: 'white',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          padding: '0.75rem 1rem',
          maxWidth: '325px',
          width: 'max-content',
          pointerEvents: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </>,
    document.body,
  );
};

// Header cell component with portal tooltip
const HeaderCell = ({ header, getSortIndicator }) => {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const iconRef = useRef(null);

  const handleIconClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsTooltipOpen(!isTooltipOpen);
  };

  return (
    <th
      key={header.id}
      style={{
        textAlign: 'center',
        cursor: header.column.columnDef.sortingFn ? 'pointer' : 'default',
        userSelect: 'none',
        position: 'sticky',
        backgroundColor: 'rgb(253, 153, 197)',
        top: 0,
        zIndex: 99999,
        overflow: 'visible',
      }}
      onClick={
        header.column.columnDef.sortingFn ? header.column.getToggleSortingHandler() : undefined
      }
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          {flexRender(header.column.columnDef.header, header.getContext())}
          {header.column.columnDef.tooltip && (
            <>
              <div ref={iconRef} onClick={handleIconClick}>
                <IoInformationCircle
                  style={{
                    color: 'white',
                    cursor: 'pointer',
                  }}
                  size={20}
                />
              </div>
              <PortalTooltip
                triggerRef={iconRef}
                isOpen={isTooltipOpen}
                onClose={() => setIsTooltipOpen(false)}
              >
                <div
                  className="markdown-body"
                  style={{ textTransform: 'initial', whiteSpace: 'pre-line' }}
                >
                  <ReactMarkdown children={header.column.columnDef.tooltip} />
                </div>
              </PortalTooltip>
            </>
          )}
        </div>
        {header.column.columnDef.sortingFn && (
          <span
            style={{
              position: 'absolute',
              bottom: 2,
              right: 2,
            }}
          >
            {getSortIndicator(header.column)}
          </span>
        )}
      </div>
    </th>
  );
};

// Helper function to recursively flatten nested objects for search
const flattenForSearch = (obj, prefix = '') => {
  const flattened = [];

  // Handle non-object inputs
  if (obj === null || obj === undefined) {
    return [''];
  }

  // Handle primitive types
  if (typeof obj !== 'object') {
    return [String(obj)];
  }

  // Handle arrays
  if (Array.isArray(obj)) {
    obj.forEach((item, index) => {
      if (typeof item === 'object' && item !== null) {
        flattened.push(...flattenForSearch(item, `${prefix}[${index}]`));
      } else {
        flattened.push(String(item || ''));
      }
    });
    return flattened;
  }

  // Handle objects
  for (const [key, value] of Object.entries(obj)) {
    const currentKey = prefix ? `${prefix}.${key}` : key;

    if (value === null || value === undefined) {
      flattened.push('');
    } else if (typeof value === 'object' && !Array.isArray(value)) {
      // Recursively flatten nested objects
      flattened.push(...flattenForSearch(value, currentKey));
    } else if (Array.isArray(value)) {
      // Handle arrays by flattening each element
      value.forEach((item, index) => {
        if (typeof item === 'object' && item !== null) {
          flattened.push(...flattenForSearch(item, `${currentKey}[${index}]`));
        } else {
          flattened.push(String(item || ''));
        }
      });
    } else {
      // Add primitive values as strings
      flattened.push(String(value));
    }
  }

  return flattened;
};

const DynamicTable = ({ data, id }) => {
  const { title, headers, rows } = data;
  const [showTable, setShowTable] = useState(true);
  const [sorting, setSorting] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedModules, setSelectedModules] = useState([]);
  const [selectedChapters, setSelectedChapters] = useState([]);
  const [selectedGrades, setSelectedGrades] = useState([]);

  const typename = rows[0]?.__typename;

  const handleShowTable = () => {
    setShowTable(!showTable);
  };

  // Enhanced columns with potential filter controls
  let columns = defaultColumnEnhancer(headers);

  const enhancer = columnEnhancers[typename];

  if (enhancer) {
    // Pass the already enhanced columns to the specific enhancer
    columns = enhancer(columns, { rows });
  }

  // Get the appropriate filter component for this table type
  const FilterComponent = getFilterComponent(typename);
  const filterControls = columns.filterControls || {};

  // Filter data based on search term and selected filters
  const filteredData = useMemo(() => {
    return rows.filter((row) => {
      // Search term filter with flattened data structure and partial word matching
      const searchMatch =
        !searchTerm ||
        flattenForSearch(row).some((value) => {
          // Ensure value is a string and handle null/undefined cases
          const stringValue = String(value || '');
          // Skip empty strings to avoid false positives
          if (!stringValue.trim()) {
            return false;
          }
          // Normalize both search term and value: replace hyphens with spaces and convert to lowercase
          const normalizedValue = stringValue.replace(/-/g, ' ').toLowerCase().trim();
          const normalizedSearch = searchTerm.replace(/-/g, ' ').toLowerCase().trim();
          // Use simple substring matching for partial word matches
          return normalizedValue.includes(normalizedSearch);
        });

      // Table-specific filtering (only if we have a filter component)
      if (FilterComponent) {
        // A-Level filtering
        if (
          typename === 'DatoCmsEdexcelALevelTopic' ||
          typename === 'DatoCmsEdexcelALevelFurtherTopic'
        ) {
          // Module filter
          const moduleMatch =
            selectedModules.length === 0 ||
            selectedModules.includes(extractModuleFromALevelRow(row));

          // Chapter filter
          const chapterMatch =
            selectedChapters.length === 0 ||
            selectedChapters.includes(extractChapterFromALevelRow(row));

          return searchMatch && moduleMatch && chapterMatch;
        }

        // GCSE filtering
        if (typename === 'DatoCmsEdexcelGcseTopic') {
          // Grade filter
          const gradeMatch =
            selectedGrades.length === 0 || selectedGrades.includes(extractGradeFromGcseRow(row));

          return searchMatch && gradeMatch;
        }
      }

      return searchMatch;
    });
  }, [
    rows,
    searchTerm,
    selectedModules,
    selectedChapters,
    selectedGrades,
    FilterComponent,
    typename,
  ]);

  const table = useReactTable({
    data: filteredData,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  // Filter handlers
  const handleModuleChange = (selectedOptions) => {
    setSelectedModules(selectedOptions ? selectedOptions.map((option) => option.value) : []);
  };

  const handleChapterChange = (selectedOptions) => {
    setSelectedChapters(selectedOptions ? selectedOptions.map((option) => option.value) : []);
  };

  const handleGradeChange = (selectedOptions) => {
    setSelectedGrades(selectedOptions ? selectedOptions.map((option) => option.value) : []);
  };

  // Simple sort indicator
  const getSortIndicator = (column) => {
    const sortDirection = column.getIsSorted();

    if (!sortDirection) {
      return <LiaArrowsAltVSolid />;
    }

    return sortDirection === 'asc' ? (
      <LiaLongArrowAltUpSolid size={18} />
    ) : (
      <LiaLongArrowAltDownSolid size={18} />
    );
  };

  return (
    <div style={{ marginBottom: '2.5rem' }}>
      <h2 style={{ textAlign: 'center', textDecoration: 'underline' }}>{title}</h2>

      <div className="topic-table-container dynamic-table" style={{ width: '100%' }}>
        <button className="button ml-0 my-1" onClick={handleShowTable}>
          {showTable ? 'Hide' : 'Show'} table
        </button>

        <div className="featured-doc-row">
          {data.featuredDocuments?.map((content) => {
            return <FeaturedDocument key={content.title} {...content} />;
          })}
        </div>

        {showTable && (
          <>
            <div
              style={{
                position: 'relative',
                marginBottom: '1rem',
                display: 'flex',
                flexDirection: 'row',
                gap: '0.75rem',
                zIndex: 9999999999,
              }}
            >
              <DebouncedInput
                value={searchTerm}
                onChange={setSearchTerm}
                className="font-lg border border-block"
                placeholder={data.searchPlaceholder || 'Search all table columns'}
                style={{
                  width: '100%',
                  maxWidth: '400px',
                  fontSize: '1rem',
                  borderRadius: '4px',
                  padding: 10,
                  border: '1px solid hsla(var(--mmc-pink))',
                }}
              />

              {FilterComponent && (
                <FilterComponent
                  filterControls={filterControls}
                  selectedModules={selectedModules}
                  selectedChapters={selectedChapters}
                  selectedGrades={selectedGrades}
                  onModuleChange={handleModuleChange}
                  onChapterChange={handleChapterChange}
                  onGradeChange={handleGradeChange}
                />
              )}
            </div>

            <div
              className="table-container"
              style={{ overflowX: 'visible', overflowY: 'auto', maxHeight: '70vh' }}
            >
              <table className="table topic-table" style={{ width: '100%' }} id={id}>
                <thead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr
                      key={headerGroup.id}
                      style={{
                        textAlign: 'center',
                        backgroundColor: 'rgb(253, 153, 197)',
                        border: '1px solid black',
                      }}
                    >
                      {headerGroup.headers.map((header) => (
                        <HeaderCell
                          key={header.id}
                          header={header}
                          getSortIndicator={getSortIndicator}
                        />
                      ))}
                    </tr>
                  ))}
                </thead>

                <tbody>
                  {table.getRowModel().rows.map((row, index) => {
                    return <RowFactory key={`${row.id || index}`} row={row} />;
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DynamicTable;
